import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PostCard } from "@/components/PostCard";
import { InFeedAdCard } from "@/components/AdUnit";
import { sortedPosts } from "@/data/posts";
import { siteConfig, categorySlug } from "@/config";

export const Route = createFileRoute("/")({
  head: () => ({
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              name: siteConfig.siteName,
              url: siteConfig.domain,
              logo: `${siteConfig.domain}/favicon.png`,
              email: siteConfig.contactEmail,
            },
            {
              "@type": "WebSite",
              name: siteConfig.siteName,
              url: siteConfig.domain,
              description: siteConfig.description,
              potentialAction: {
                "@type": "SearchAction",
                target: `${siteConfig.domain}/search?q={search_term_string}`,
                "query-input": "required name=search_term_string",
              },
            },
          ],
        }),
      },
    ],
  }),
  component: Home,
});

function Home() {
  const posts = sortedPosts();

  const cards: React.ReactNode[] = [];
  posts.forEach((p, i) => {
    cards.push(<PostCard key={p.id} post={p} priority={i === 0} />);
    if (i === 2 || (i > 2 && (i - 2) % 6 === 0)) {
      cards.push(<InFeedAdCard key={`ad-${i}`} />);
    }
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="border-b border-border bg-card">
          <div className="mx-auto max-w-6xl px-4 py-16">
            <div className="max-w-3xl">
              <p className="mb-3 text-sm font-medium uppercase tracking-widest text-primary">
                CRM Business Blog
              </p>
              <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
                Honest CRM reviews, comparisons, and buyer's guides.
              </h1>
              <p className="mt-4 text-lg text-muted-foreground">
                {siteConfig.siteName} helps founders, sales managers, and RevOps
                leads pick the CRM that actually fits their pipeline. Every
                review starts with a hands-on pilot and interviews with
                operators running the tools in production — not marketing pages.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                <Link
                  to="/about-us"
                  className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90"
                >
                  How we test
                </Link>
                <Link
                  to="/editorial-policy"
                  className="rounded-md border border-border px-4 py-2 font-medium hover:bg-muted"
                >
                  Editorial policy
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section
          aria-label="Browse topics"
          className="border-b border-border"
        >
          <div className="mx-auto max-w-6xl px-4 py-10">
            <h2 className="mb-4 text-lg font-semibold">Browse by topic</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {siteConfig.categories.map((c) => (
                <Link
                  key={c}
                  to="/category/$category"
                  params={{ category: categorySlug(c) }}
                  className="rounded-lg border border-border bg-card p-4 text-sm font-medium transition-colors hover:border-primary hover:bg-muted"
                >
                  {c}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-12">
          <h2 className="mb-6 text-2xl font-bold tracking-tight">
            Latest articles
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{cards}</div>
        </section>

        <section className="border-t border-border bg-card">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 md:grid-cols-3">
            <div>
              <h3 className="text-lg font-semibold">Hands-on testing</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Every product we cover is set up with a working pipeline, live
                email sync, and at least one automation before we publish. We
                test what changes after the demo call ends.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Independent editorial</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Vendors do not preview or approve our reviews. Rankings are set
                by the editorial team based on the pilot — never by advertising
                or affiliate arrangements.{" "}
                <Link to="/editorial-policy" className="underline">
                  Read our policy
                </Link>
                .
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Written for operators</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {siteConfig.siteName} is written for the people actually running
                the pipeline — sales managers, RevOps leads, and founders.
                Every guide is grounded in interviews with operators using the
                tools in production.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

